import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import * as actionType from '../context/actions';
import SoldItemsTableDataLayer from './SoldItemsTableDataLayer';
import { ConfigContext } from '../context/GlobalContext';
import { useNavigate } from 'react-router-dom';
import BusinessDropdown from './child/BusinessDropdown';
import { Languages } from '../Utils/Languages';
import { Icon } from '@iconify/react';
import Currancy from "../Utils/currency.json";

const SoldItemsListLayer = ({ SoldItemsData }) => {
    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();

    const [selectedBusiness, setSelectedBusiness] = useState("null");
    const [searchText, setSearchText] = useState("");

    // Currency selection state
    const [selectedCurrency, setSelectedCurrency] = useState("");
    const [currencyOpen, setCurrencyOpen] = useState(false);
    const currencyRef = useRef(null);

    // Close currency dropdown on click outside
    useEffect(() => {
        const handler = (e) => {
            if (currencyRef.current && !currencyRef.current.contains(e.target)) {
                setCurrencyOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ===========================
    // PLAN CONFIG
    // ===========================
    const starterPlanName = process.env.REACT_APP_STARTER_PLAN_NAME;
    const professionalPlanName = process.env.REACT_APP_PROFESSIONAL_PLAN_NAME;
    const agencyPlanName = process.env.REACT_APP_AGENCY_PLAN_NAME;

    const userPlanName = state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";

    const isStarterPlan = userPlanName === starterPlanName;
    const isProfessionalPlan = userPlanName === professionalPlanName;
    const isAgencyPlan = userPlanName === agencyPlanName;

    // ===========================
    // SORT BUSINESSES (Oldest → Newest)
    // ===========================
    const sortedBusinesses = useMemo(() => {
        return [...(state?.BUSINESSES || [])]
            .sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
    }, [state?.BUSINESSES]);

    // ===========================
    // BUSINESS ACCESS LIMIT
    // ===========================
    let allowedBusinessCount = 1; // Free & Starter

    if (isProfessionalPlan) {
        allowedBusinessCount = 3;
    }

    if (isAgencyPlan) {
        allowedBusinessCount = sortedBusinesses.length;
    }

    const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
    const sharedBusinesses = sortedBusinesses.filter(b => b.isShared);

    const allowedOwnIds = ownBusinesses
        .slice(-allowedBusinessCount)
        .map(b => String(b.companyId));
    const sharedIds = sharedBusinesses.map(b => String(b.companyId));

    const allAllowedIds = [...allowedOwnIds, ...sharedIds];

    // Extract unique currencies from the selected business's invoices and customers
    const uniqueCurrencies = useMemo(() => {
        const currencies = new Set();
        const bizFilter = selectedBusiness !== "null" ? [String(selectedBusiness)] : allAllowedIds;

        (state?.INVOICES || []).forEach(inv => {
            if (inv.currency && bizFilter.includes(String(inv.companyId))) {
                currencies.add(inv.currency);
            }
        });
        (state?.CUSTOMERS || []).forEach(cust => {
            if (cust.currency && bizFilter.includes(String(cust.companyId))) {
                currencies.add(cust.currency);
            }
        });
        return Array.from(currencies);
    }, [state?.INVOICES, state?.CUSTOMERS, selectedBusiness, allAllowedIds]);

    // Automatically select the first available currency or fallback to state currency code
    useEffect(() => {
        const fallback = state?.CURRENCYCODE || "INR";
        if (uniqueCurrencies.length > 0) {
            if (!selectedCurrency || !uniqueCurrencies.includes(selectedCurrency)) {
                setSelectedCurrency(uniqueCurrencies[0]);
            }
        } else {
            if (selectedCurrency !== fallback) {
                setSelectedCurrency(fallback);
            }
        }
    }, [uniqueCurrencies, state?.CURRENCYCODE, selectedCurrency]);

    const getCurrencyDisplay = (code) => {
        const matched = Currancy?.CountryCurrencyResponse?.find(c => c.currencyCode === code);
        if (matched) {
            return `${matched.currencyCode} (${matched.currencytSymbol})`;
        }
        return code;
    };



    // ===========================
    // SYNC SELECTED BUSINESS
    // ===========================
    useEffect(() => {
        if (!state?.BUSINESSES?.length) return;

        if (state?.SELECTEDBUSINESS === "null") {
            setSelectedBusiness("null");
            return;
        }

        const selected =
            state?.BUSINESSES?.find(b => b?.companyId == state?.SELECTEDBUSINESS)?.companyId ||
            "null";

        setSelectedBusiness(selected);
    }, [state?.BUSINESSES, state?.SELECTEDBUSINESS]);

    const handleSelection = (businessId) => {
        setSelectedBusiness(businessId);
        dispatch({ type: actionType.SELECTEDBUSINESS, payload: businessId });
    };

    return (
        <div className="card basic-data-table">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <BusinessDropdown
                        businesses={sortedBusinesses}
                        selectedCompanyId={state?.SELECTEDBUSINESS}
                        onBusinessChange={handleSelection}
                        onPricingNavigate={() => navigate("/pricing")}
                        showAllOption={true}
                        isAllowedCheck={(business) => {
                            const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
                            const allowedOwnIds = ownBusinesses
                                .slice(-allowedBusinessCount)
                                .map(b => b.companyId);

                            return (
                                business.isShared ||
                                isAgencyPlan ||
                                allowedOwnIds.includes(business.companyId)
                            );
                        }}
                    />

                    {/* Dynamic Currency Filter Dropdown */}
                    {uniqueCurrencies.length > 1 && (
                        <div ref={currencyRef} className="dropdown custom-currency-dropdown" style={{ position: 'relative' }}>
                            <button
                                className="btn btn-outline-primary-600 not-active px-18 py-11 dropdown-toggle toggle-icon d-flex align-items-center justify-content-between gap-2 rounded"
                                type="button"
                                onClick={() => setCurrencyOpen(prev => !prev)}
                                style={{
                                    minWidth: '180px',
                                    maxWidth: '220px',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <span className="text-truncate" style={{ fontSize: '14px', fontWeight: '500' }}>
                                    {getCurrencyDisplay(selectedCurrency)}
                                </span>
                            </button>

                            {currencyOpen && (
                                <ul className="dropdown-menu show w-100 mt-1 shadow-lg rounded-8 text-start p-0"
                                    style={{
                                        zIndex: 1050,
                                        minWidth: '100%',
                                        position: 'absolute',
                                        left: 0,
                                        backgroundColor: localStorage.getItem("theme") === "dark" ? '#1b2431' : '#fff',
                                        border: localStorage.getItem("theme") === "dark" ? '1px solid #334155' : 'none'
                                    }}>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="hide-scrollbar">
                                        {uniqueCurrencies.map(curr => {
                                            const isSelected = curr === selectedCurrency;
                                            const isDark = localStorage.getItem("theme") === "dark";
                                            const textColor = isDark ? '#f1f5f9' : '#475569';
                                            const hoverBg = isDark ? '#334155' : '#F1F5F9';
                                            
                                            return (
                                                <li key={curr}>
                                                    <button
                                                        className={`dropdown-item px-16 py-8 rounded d-flex align-items-center gap-2 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900
                                                            ${isSelected ? 'bg-primary-50 fw-semibold text-primary-600' : ''}`}
                                                        onClick={() => {
                                                            setSelectedCurrency(curr);
                                                            setCurrencyOpen(false);
                                                        }}
                                                        style={{
                                                            fontSize: '14px',
                                                            color: isSelected ? '#487fff' : textColor,
                                                            backgroundColor: isSelected ? (isDark ? '#1e293b' : '#f0f4ff') : 'transparent'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? (isDark ? '#1e293b' : '#f0f4ff') : 'transparent'}
                                                    >
                                                        <span className="flex-grow-1 text-truncate">
                                                            {getCurrencyDisplay(curr)}
                                                        </span>
                                                        {isSelected && (
                                                            <Icon icon="lucide:check" className="text-primary-600 text-lg ms-auto" />
                                                        )}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </div>
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                {/* Search input in header */}
                <div className="d-flex align-items-center position-relative">
                    <Icon
                        icon="ion:search-outline"
                        className="position-absolute text-secondary-light"
                        style={{ left: 10, fontSize: 16, pointerEvents: "none" }}
                    />
                    <input
                        type="text"
                        className="form-control"
                        style={{ paddingLeft: 32, minWidth: 220 }}
                        placeholder={Languages[state?.LANGUAGE]?.search_sold_items || "Search sold items..."}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            </div>

            <SoldItemsTableDataLayer 
                SoldItemsData={SoldItemsData} 
                selectedBusiness={selectedBusiness}
                allAllowedIds={allAllowedIds}
                searchText={searchText}
                selectedCurrency={selectedCurrency}
            />
        </div>
    );
};

export default SoldItemsListLayer;